/**
 * debug.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { ActivatedRoute, Event as RouterEvent, Router }
  from "@angular/router";

import { AlgorithmStepService }
  from "../algorithms/management/algorithm-step.service";
import { Alert, AlertService } from "../common/alert.service";
import { MessageReceiver } from  "../server/message-receiver";
import {
  AnnouncementBroadcast,
  NotificationLevel,
  NotifyDownstreamMessage,
  QuarantineConfigurationResponse,
  ShutdownResponse
} from "../server/messages/admin/";
import {
  DebugBreakpointUpstreamMessage, DebugLogDownstreamMessage
} from "../server/messages/debug/";
import { Message, MessageTag } from "../server/messages/general/";
import { MessagingService } from "../server/messaging.service";
import { Subject } from "../subjects/subject";
import { SubjectService } from "../subjects/subject.service";
import { StorageService } from "../support/APIs/storage.service";
import { WatchlistService } from "../watchlist/watchlist.service";

enum LogLevel {
  Silent = 0,
  Normal,
  Interrupt
}

interface StartupTarget
{
  type : "watchlist";
  id : number;
}

/**
 * The {@link DebugService} handles special messages related to debugging,
 * error-handling, and maintenance of the application.
 */
@Injectable()
export class DebugService implements MessageReceiver
{
  /**
   * A list of log messages received from the server.
   */
  public messages : any[] = [];

  /**
   * The current notification level to use for this service's logging.
   */
  public level : LogLevel = LogLevel.Normal;

  constructor (
    protected messaging : MessagingService,
    protected route : ActivatedRoute,
    protected alerts : AlertService,
    protected steps : AlgorithmStepService,
    protected watchlist : WatchlistService,
    protected storage : StorageService,
    protected subjects : SubjectService
  )
  {
    this.messaging.ready.subscribe(
      (on : boolean) => on ? this.connected() : this.disconnected());
    this.watchlist.hasPersonalList.subscribe(
      (watchlistLoaded : boolean) =>
        watchlistLoaded ? this.loadStartupTarget() : null);
  }

  /**
   * Initialization for when the {@link MessagingService} is ready.
   */
  public connected () : void
  {
    this.messaging.receive(MessageTag.DBG_DOWN_LOG).subscribe(
      (m : DebugLogDownstreamMessage) => this.handleDebugMessage(m));
    this.messaging.receive(MessageTag.DOWN_NOTIFY).subscribe(
      (m : NotifyDownstreamMessage) =>
        this.presentNotification(m.message, m.level));
    this.messaging.receive(MessageTag.BCST_ANNOUNCEMENT).subscribe(
      (m : AnnouncementBroadcast) => this.presentNotification(m.message));
    this.messaging.receive(MessageTag.RSP_CFG_QUARANTINE).subscribe(
      (m : QuarantineConfigurationResponse) => {
        if (m.lockPending)
        {
          this.presentCountdown(
            QuarantineConfigurationResponse.StandardMessage,
            m.secondsTillLock
          );
        }
        else
        {
          this.cancelCountdown();
        }
      });
    this.messaging.receive(MessageTag.RSP_SHUTDOWN).subscribe(
      (m : ShutdownResponse) => {
        if (m.shutdownPending)
        {
          this.presentCountdown(
            ShutdownResponse.StandardMessage,
            m.secondsTillShutdown
          );
        }
        else
        {
          this.cancelCountdown();
        }
      });
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected () : void
  {
    // Nothing
  }

  /**
   * Record incoming log messages and present them to the user as necessary.
   * @param message a server-generated log message
   */
  private handleDebugMessage (message : DebugLogDownstreamMessage) : void
  {
    // First record the content in our array...
    this.messages.push(message.content);

    if (message.content.badAnswer)
    {
      this.badAnswer(message.content.badAnswer);
    }

    // Then log the content at the appropriate volume...
    if (this.level === LogLevel.Interrupt)
    {
      // Without the JSON conversion, alert will show [Object object] for most
      // entities. Note that stringify can't handle recursion, but the object
      // came to us over the wire, so we know it's not recursive.
      return alert(JSON.stringify(message.content));
    }
    if (this.level === LogLevel.Normal)
    {
      return console.log("[PresentDebugMessage]:", message.content);
    }
    // If we're at LogLevel.Silent, do nothing.
  }

  /**
   * Show a message or announcement to the client.
   * @param message the string to present
   * @param level a log level, determining the style of alert to present
   */
  private presentNotification (
    message : string, level : NotificationLevel = NotificationLevel.Info
  ) : boolean
  {
    switch (level)
    {
      case NotificationLevel.Error:
        return this.alerts.add({message, type: "error"});
      case NotificationLevel.Urgent:
        return this.alerts.add({message, type: "warning"});
      default:
        return this.alerts.add({message, type: "notification"});
    }
  }

  /**
   * A reference to the last active countdown-based alert presented to the user.
   */
  private countdownAlert : Alert;
  /**
   * Create a countdown-based alert, usually in conjunction with notification of
   * server maintenance or other behavior.
   * @param message string description of the event
   * @param seconds seconds remaining in the countdown
   */
  private presentCountdown (
    message : string, seconds : number
  ) : void
  {
    this.cancelCountdown();
    this.countdownAlert = {message, type: "countdown", timeout: seconds * 1000};
    this.alerts.add(this.countdownAlert);
  }
  /**
   * Cancel and remove the current countdown-based alert, if one exists.
   */
  private cancelCountdown () : void
  {
    if (this.countdownAlert)
    {
      this.alerts.remove(this.countdownAlert);
    }
  }

  /**
   * Alert the user that an answer they submitted was invalid.
   * @param answer the provided (and malformed) answer
   */
  private badAnswer (answer : any) : void
  {
    if (answer && Number.isInteger(answer.id) && this.steps.has(answer.id))
    {
      const stepName : string = this.steps.get(answer.id).name;
      this.alerts.error(
        `The server indicated your answer for <tt>${stepName}</tt> was invalid`
      );
    }
    else
    {
      this.alerts.error(`The server indicated your answer was invalid`);
    }
  }

  /**
   * Falsify an incoming message, as if it had been sent by the server
   * @param message the incoming message to mock
   */
  public mock (message : Message) : void
  {
    if (!this.messaging.mockInput)
    {
      throw new Error("Mock input not available on MessagingService!");
    }
    this.messaging.mockInput.receive(message);
  }

  /**
   * Request the server to halt at a breakpoint, if in debug mode.
   */
  public breakpoint () : void
  {
    this.messaging.send(new DebugBreakpointUpstreamMessage()).subscribe();
  }

  public static readonly SSTKey : string = "debug:startupTarget";

  public startupSubjectTarget : StartupTarget =
    this.storage.getWrappedValue<StartupTarget, StartupTarget>(
      DebugService.SSTKey);

  public loadStartupTarget () : void
  {
    if (this.startupSubjectTarget)
    {
      if (this.startupSubjectTarget.type === "watchlist")
      {
        this.watchlist.open(
            this.watchlist.personalEntriesById.get(
              this.startupSubjectTarget.id)
          ).subscribe(
            () => this.alerts.success("Redirecting to debug startup target"));
      }
      else
      {
        throw new Error(
          `Unknown startup target type "${this.startupSubjectTarget.type}"`);
      }
    }
    else
    {
      return;
    }
  }

  public setStartupTarget (subject : Subject) : void
  {
    if (subject.incomingWatchId)
    {
      this.startupSubjectTarget = {
        type: "watchlist",
        id: subject.incomingWatchId
      };
      this.storage.setWrapped(DebugService.SSTKey, this.startupSubjectTarget);
    }
    else
    {
      alert("Can only set debug-startup for subjects on your watchlist");
    }
  }

  public clearStartupTarget () : void
  {
    this.startupSubjectTarget = null;
    this.storage.setWrapped(DebugService.SSTKey, null);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/debug/debug.service.ts